import { useMemo, useState } from 'react'
import { ArrowDown, ArrowRight, Check, CircleDot, Database, Menu, ShieldCheck, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { EChart } from '../components/EChart'
import { BrandLockup, MetricCard, SectionHeading } from '../components/ui'
import { usePublicDemo } from '../data/PublicDemoContext'
import { cohortHeatmapOption, executiveTrendOption, heroTrendOption, productPortfolioOption } from '../lib/chartOptions'
import { dateLabel, integer, money, percent, signedPercent } from '../lib/format'
import type { PublicDemoBundle } from '../types'
import { LanguageToggle, useI18n } from '../i18n'
import { DataModelPreview } from '../components/DataModelExplorer'

export function LandingPage() {
  const data = usePublicDemo()
  const [menuOpen, setMenuOpen] = useState(false)
  const { copy, locale, path } = useI18n()
  const landingNav = Object.entries(copy.landing.nav)

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <header className="site-header">
        <BrandLockup />
        <button className="menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-controls="site-navigation" aria-label={copy.common.toggleNavigation}>
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <nav id="site-navigation" className={menuOpen ? 'site-nav open' : 'site-nav'} aria-label={copy.common.landingNavigation}>
          {landingNav.map(([id, navLabel]) => <a key={id} href={`#${id}`} onClick={() => setMenuOpen(false)}>{navLabel}</a>)}
        </nav>
        <div className="site-header-actions"><LanguageToggle compact /><Link to={path('/analytics/overview')} className="workspace-link"><span>{copy.common.analyticsWorkspace}</span><ArrowRight size={15} /></Link></div>
      </header>

      <main>
        <Hero data={data} />
        <ExecutivePulse data={data} />
        <Platform data={data} />
        <FeaturedAnalytics data={data} />
        <DomainPreviews data={data} />
        <Trust data={data} />
        <Engineering />
      </main>

      <footer className="site-footer">
        <div><strong>Commerce Intelligence</strong><span>{copy.landing.footerEvidence}</span></div>
        <p>{copy.common.dataAsOf} {dateLabel(data.data_as_of, locale)} · {copy.landing.publicContract} v{data.contract_version}</p>
      </footer>
    </div>
  )
}

function Hero({ data }: { data: PublicDemoBundle }) {
  const { copy, locale, path } = useI18n()
  const option = useMemo(() => heroTrendOption(data.overview.weekly_performance), [data])
  return (
    <section id="overview" className="hero-section">
      <div className="hero-chart" aria-hidden="true"><EChart option={option} className="h-full w-full" ariaLabel={copy.landing.pulse.trendTitle} /></div>
      <div className="hero-content page-shell">
        <div className="hero-eyebrow"><span>{copy.common.syntheticData.toUpperCase()}</span><span className="hero-dot" /><span>{copy.landing.hero.evidence}</span></div>
        <h1>{copy.landing.hero.line1}<br/><span>{copy.landing.hero.line2}</span></h1>
        <p>{copy.landing.hero.copy}</p>
        <div className="hero-actions">
          <Link to={path('/analytics/overview')} className="primary-action">{copy.landing.hero.explore} <ArrowRight size={16} /></Link>
          <a href="#platform" className="secondary-action">{copy.landing.hero.platform} <ArrowDown size={16} /></a>
        </div>
        <div className="hero-proof" aria-label={copy.common.platformProof}>
          <Proof value={`${data.trust.governed_metrics}`} label={copy.landing.hero.governedMetrics} />
          <Proof value={`${data.trust.certified_datasets}`} label={copy.landing.hero.certifiedDatasets} />
          <Proof value={`${data.trust.management_modules}`} label={copy.landing.hero.managementModules} />
          <Proof value={dateLabel(data.data_as_of, locale)} label={copy.landing.hero.dataAsOf} />
        </div>
      </div>
    </section>
  )
}

function Proof({ value, label }: { value: string; label: string }) {
  return <div><strong>{value}</strong><span>{label}</span></div>
}

function ExecutivePulse({ data }: { data: PublicDemoBundle }) {
  const { copy, locale } = useI18n()
  const ids = ['net_sales_after_returns', 'contribution_profit', 'orders', 'gross_margin_pct']
  const metrics = ids.map((id) => data.overview.metrics.find((metric) => metric.metric_id === id)!)
  const trend = useMemo(() => executiveTrendOption(data.overview.weekly_performance, true, locale), [data, locale])
  return (
    <section className="page-section pulse-section" aria-labelledby="executive-pulse-title">
      <div className="page-shell">
        <div className="pulse-heading">
          <div><span className="eyebrow">{copy.landing.pulse.eyebrow}</span><h2 id="executive-pulse-title">{copy.landing.pulse.headline}</h2></div>
          <div className="pulse-copy"><p>{copy.landing.pulse.support}</p><span>{copy.landing.pulse.period}</span></div>
        </div>
        <div className="pulse-layout">
          <div className="pulse-metrics">{metrics.map((metric) => <MetricCard key={metric.metric_id} metric={metric} compact />)}</div>
          <article className="pulse-trend">
            <div><span>{copy.landing.pulse.trendLabel}</span><strong>{copy.landing.pulse.trendTitle}</strong></div>
            <EChart option={trend} className="pulse-trend-chart" ariaLabel={copy.landing.pulse.trendTitle} />
          </article>
        </div>
      </div>
    </section>
  )
}

function Platform({ data }: { data: PublicDemoBundle }) {
  const { copy } = useI18n()
  return (
    <section id="platform" className="page-section platform-section">
      <div className="page-shell">
        <SectionHeading eyebrow={copy.landing.platform.eyebrow} title={copy.landing.platform.title} copy={copy.landing.platform.copy} />
        <div className="architecture-flow">
          {data.platform.stages.map((stage, index) => (
            <div className="architecture-item" key={stage.id}>
              <div className="architecture-node"><span>{String(index + 1).padStart(2, '0')}</span><strong>{copy.landing.platform.stages[stage.id as keyof typeof copy.landing.platform.stages][0]}</strong><small>{copy.landing.platform.stages[stage.id as keyof typeof copy.landing.platform.stages][1]}</small></div>
              {index < data.platform.stages.length - 1 && <ArrowRight className="architecture-arrow" size={18} aria-hidden="true" />}
            </div>
          ))}
        </div>
        <div className="truth-note"><Database size={18} /><p>{copy.landing.platform.truth}</p></div>
        <DataModelPreview />
      </div>
    </section>
  )
}

function FeaturedAnalytics({ data }: { data: PublicDemoBundle }) {
  const { copy, locale } = useI18n()
  const portfolioOption = useMemo(() => productPortfolioOption(data.product_portfolio, locale), [data, locale])
  const cohortOption = useMemo(() => cohortHeatmapOption(data.customer_cohorts, locale), [data, locale])
  return (
    <section id="analytics" className="page-section">
      <div className="page-shell">
        <SectionHeading eyebrow={copy.landing.featured.eyebrow} title={copy.landing.featured.title} copy={copy.landing.featured.copy} />
        <article className="analysis-panel">
          <div className="analysis-header">
            <div><span className="analysis-number">01</span><h3>{copy.landing.featured.portfolioTitle}</h3><p>{copy.landing.featured.portfolioCopy}</p></div>
            <div className="reference-facts"><span>{copy.landing.featured.medians}</span><strong>{money(data.product_portfolio.median_sales, 1, locale)} {copy.landing.featured.sales}</strong><strong>{percent(data.product_portfolio.median_gross_margin)} {copy.landing.featured.margin}</strong></div>
          </div>
          <EChart option={portfolioOption} className="portfolio-chart" ariaLabel={copy.landing.featured.portfolioTitle} />
          <div className="chart-note"><CircleDot size={14} />{copy.landing.featured.portfolioNote}</div>
        </article>
        <article className="analysis-panel cohort-panel">
          <div className="analysis-header">
            <div><span className="analysis-number">02</span><h3>{copy.landing.featured.cohortTitle}</h3><p>{copy.landing.featured.cohortCopy}</p></div>
            <div className="reference-facts"><span>{copy.landing.featured.cohortTruth}</span><strong>{data.customer_cohorts.cohorts.length} {copy.landing.featured.acquisitionCohorts}</strong><strong>{copy.landing.featured.repeatActivity}</strong></div>
          </div>
          <EChart option={cohortOption} className="cohort-chart" ariaLabel={copy.landing.featured.cohortTitle} />
          <div className="chart-note"><CircleDot size={14} />{copy.landing.featured.cohortNote}</div>
        </article>
      </div>
    </section>
  )
}

function DomainPreviews({ data }: { data: PublicDemoBundle }) {
  const { copy, locale, path } = useI18n()
  const netSales = data.overview.metrics.find((metric) => metric.metric_id === 'net_sales_after_returns')!
  const activeCustomers = data.overview.metrics.find((metric) => metric.metric_id === 'active_customers')!
  const portfolioSales = data.product_portfolio.products.reduce((sum, product) => sum + product.current_sales, 0)
  const modules = [
    { id: 'executive', label: copy.navigation.overview, value: money(netSales.current_value, 1, locale), detail: `${signedPercent(netSales.relative_change)} ${copy.metricLabels.net_sales_after_returns}`, cue: 78, tone: 'teal', path: '/analytics/overview' },
    { id: 'sales', label: copy.navigation.sales, value: money(portfolioSales, 1, locale), detail: copy.landing.modules.currentPortfolioSales, cue: 71, tone: 'navy', path: '/analytics/sales' },
    { id: 'customer', label: copy.navigation.customer, value: integer(activeCustomers.current_value, locale), detail: `${signedPercent(activeCustomers.relative_change)} ${copy.landing.modules.activeCustomers}`, cue: 66, tone: 'green', path: '/analytics/customer' },
    { id: 'marketing', label: copy.navigation.marketing, value: `${data.domain_previews.marketing.paid_roas.toFixed(2)}×`, detail: `${money(data.domain_previews.marketing.spend, 1, locale)} ${copy.landing.modules.paidSpend}`, cue: 62, tone: 'amber', path: '/analytics/marketing' },
    { id: 'inventory', label: copy.navigation.inventory, value: integer(data.domain_previews.inventory.coverage_risk_positions, locale), detail: `${integer(data.domain_previews.inventory.reorder_gap_units, locale)} ${copy.landing.modules.reorderGap} · 0 ${copy.landing.modules.zeroStock}`, cue: 45, tone: 'coral', path: '/analytics/inventory' },
    { id: 'operations', label: copy.navigation.operations, value: percent(data.domain_previews.operations.on_time_delivery_rate), detail: `${integer(data.domain_previews.operations.delivered_orders, locale)} ${copy.landing.modules.deliveredOrders}`, cue: 85, tone: 'green', path: '/analytics/operations' },
    { id: 'trust', label: copy.navigation.metrics, value: `${data.trust.certified_metrics}/${data.trust.governed_metrics}`, detail: copy.landing.modules.certification, cue: 98, tone: 'navy', path: '/analytics/metrics' },
  ]
  return (
    <section className="page-section domain-section">
      <div className="page-shell">
        <SectionHeading eyebrow={copy.landing.modules.eyebrow} title={copy.landing.modules.title} copy={copy.landing.modules.copy} />
        <div className="domain-grid-v2">
          {modules.map((module, index) => {
            const content = <><div className="domain-card-top"><span>{module.label}</span><small>{copy.common.openModule.toUpperCase()}</small></div><strong>{module.value}</strong><p>{module.detail}</p><div className="domain-cue"><i style={{ width: `${module.cue}%` }} /></div><b>{copy.landing.modules.explore} <ArrowRight size={14} /></b></>
            const className = `domain-entry ${module.tone}${index === 0 ? ' featured' : ''}`
            return <Link to={path(module.path)} className={className} key={module.id}>{content}</Link>
          })}
        </div>
      </div>
    </section>
  )
}

function Trust({ data }: { data: PublicDemoBundle }) {
  const { copy, label } = useI18n()
  return (
    <section id="trust" className="page-section trust-section">
      <div className="page-shell">
        <SectionHeading eyebrow={copy.landing.trust.eyebrow} title={copy.landing.trust.title} copy={copy.landing.trust.copy} />
        <div className="trust-layout">
          <div className="trust-flow">{copy.landing.trust.flow.map((flowLabel, index) => <div className="trust-step" key={flowLabel}><span>{index + 1}</span><strong>{flowLabel}</strong>{index < copy.landing.trust.flow.length - 1 && <ArrowRight size={18} />}</div>)}</div>
          <div className="trust-score"><span>{copy.landing.trust.coverage}</span><strong>{data.trust.certified_metrics}<small> / {data.trust.governed_metrics}</small></strong><div className="coverage-bar"><span style={{ width: `${data.trust.certified_metrics / data.trust.governed_metrics * 100}%` }} /></div><p><Check size={16} />{copy.landing.trust.reconciliation}: {label(data.trust.reconciliation_status)}</p></div>
        </div>
        <div className="deferred-note"><span>{copy.landing.trust.limitation}</span><strong>{copy.landing.trust.deferredName}</strong><p>{copy.landing.trust.deferredCopy}</p></div>
      </div>
    </section>
  )
}

function Engineering() {
  const { copy } = useI18n()
  return (
    <section id="engineering" className="page-section engineering-section">
      <div className="page-shell">
        <SectionHeading eyebrow={copy.landing.engineering.eyebrow} title={copy.landing.engineering.title} copy={copy.landing.engineering.copy} />
        <div className="engineering-grid">{copy.landing.engineering.controls.map((item, index) => <article key={item[0]}><span>{String(index + 1).padStart(2, '0')}</span><h3>{item[0]}</h3><p>{item[1]}</p></article>)}</div>
        <div className="closing-statement"><ShieldCheck size={26} /><div><strong>{copy.landing.engineering.closing}</strong><p>{copy.landing.engineering.closingCopy}</p></div></div>
      </div>
    </section>
  )
}
